import { memo, useEffect, useState } from 'react'
import { EnhancedPaper, Spinner, showNotification } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { Button, Center, Divider, Group, Modal, Stack, Title, Text } from 'summon-ui/mantine'
import { useBlockNumber, useReadContract } from 'wagmi'
import { graphqlClient } from '@api/config'
import { BridgeLink } from '@components'
import { TENANT_ONBOARDING, TENANT_CONTRACTS, TENANT_CONFIG } from '@config'
import { AVATAR_BOUND_ABI } from '@constants/Abi/avatarBoundAbi'
import AvatarWithInventory from '@features/onboarding/components/AvatarWithInventory'
import OnboardingHeader from '@features/onboarding/components/OnboardingHeader'
import useOnboardingUtils from '@features/onboarding/hooks/useOnboardingUtils'
import useSponsoredAvatarMint from '@features/onboarding/hooks/useSponsoredAvatarMint'
import { useOnboardingStorage } from '@features/onboarding/onboarding.store'
import { useEnsureLevel0ClaimableItemExistMutation, useMarkClaimableItemAsUsedMutation } from '@generated/generates'
import withWaitingRoom from '@hocs/withWaitingRoom'
import { useAddOnchainHistoryLog, useLogout, useMintTypeBased, useViewer } from '@hooks'
import { MintType } from '@hooks/useMintTypeBased'
import { isStaging } from '@utils/isStaging'

const CONFIRM_MODAL_DESCRIPTION =
  'By minting you will receive a soulbound avatar, as well as several equippable NFT items.'

const MintAvatarOnchain = () => {
  const t = useTranslation()
  const { viewer } = useViewer()
  const { logout } = useLogout()
  const { setStep, selectedOnboardingSkin, step } = useOnboardingStorage()
  const { saveAvatar } = useOnboardingUtils()
  const { addOnchainHistoryLog } = useAddOnchainHistoryLog()
  const [nonce, setNonce] = useState<null | undefined | number>(null)
  const [signature, setSignature] = useState<null | undefined | string>(null)
  const [randomDataString, setRandomDataString] = useState<null | undefined | string>(null)
  const [initialClaimableId, setInitialClaimableId] = useState<string | null>(null)
  const [isMainnet, setIsMainnet] = useState(false)
  const [isMintModalOpen, setIsMintModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [mintType, setMintType] = useState(MintType.Eth)
  const { hasBalanceToSponsor, mintAvatarMutation } = useSponsoredAvatarMint()

  const handleFinishStep = () => {
    setIsMintModalOpen(false)
    setIsLoading(false)
    setStep(1)
  }

  const { mutateAsync: markAsClaimed } = useMarkClaimableItemAsUsedMutation(graphqlClient(), {
    onSuccess() {
      handleFinishStep()
    }
  })

  // Get the data needed for mint avatar + id to mark the first claimable as used after minting
  const { mutateAsync: getDataToPrepareMint } = useEnsureLevel0ClaimableItemExistMutation(graphqlClient(), {
    onSuccess(data) {
      const { id, nonce, randomData, signature } = data.ensureLevel0ClaimableItemExist
      setInitialClaimableId(id)
      setNonce(nonce)
      setSignature(signature)
      setIsMainnet(data.ensureLevel0ClaimableItemExist?.data?.isMainnet)
      setRandomDataString(randomData)
    },
    onError(error) {
      console.error('Error preparing mint: ', error)
    }
  })

  const baseSkinId = Number(selectedOnboardingSkin?.id)

  const { mint, errorMessage } = useMintTypeBased({
    mintType,
    functionName: 'mintAvatar',
    args: [baseSkinId, nonce, randomDataString, signature],
    contract: isMainnet ? TENANT_CONTRACTS.mainnet.avatar : TENANT_CONTRACTS.testnet.avatar,
    abi: AVATAR_BOUND_ABI,
    isMainnet
  })

  const { data: tokenIdData, refetch } = useReadContract({
    address: isStaging ? TENANT_CONTRACTS.testnet.avatar : TENANT_CONTRACTS.mainnet.avatar,
    abi: AVATAR_BOUND_ABI,
    chainId: TENANT_CONFIG.network.id,
    functionName: 'tokenOfOwnerByIndex',
    args: [viewer?.walletAddress, 0],
    query: { enabled: !!viewer?.walletAddress }
  })
  const { data: blockNumber } = useBlockNumber({ watch: !tokenIdData, chainId: TENANT_CONFIG.network.id })

  useEffect(() => {
    if (!tokenIdData) {
      refetch()
    } else {
      const saveAndMarkAsClaimed = async () => {
        await saveAvatar({ tokenId: Number(tokenIdData) as number, skinId: selectedOnboardingSkin?.glbUrl as string })

        if (!hasBalanceToSponsor && initialClaimableId) {
          await markAsClaimed({ markClaimableItemAsUsedId: initialClaimableId as string })
        } else {
          handleFinishStep()
        }
      }
      saveAndMarkAsClaimed()
    }
  }, [blockNumber, tokenIdData])

  useEffect(() => {
    if (viewer?.walletAddress && hasBalanceToSponsor != null && !hasBalanceToSponsor) {
      getDataToPrepareMint({})
    }
  }, [viewer?.walletAddress, hasBalanceToSponsor])

  useEffect(() => {
    if (errorMessage) {
      setIsMintModalOpen(false)
    }
  }, [errorMessage])

  const sponsoredMint = async () => {
    try {
      setIsLoading(true)
      await mintAvatarMutation({ baseSkinId })
    } catch (error) {
      setIsLoading(false)
      showNotification({
        variant: 'danger',
        message: 'Could not mint the Avatar'
      })
    }
  }

  const gasMint = async () => {
    try {
      setIsLoading(true)
      const tx = await mint()

      await addOnchainHistoryLog({
        txAddress: tx.transactionHash,
        contractAddress: tx.to,
        description: 'Avatar minted'
      })
    } catch (error) {
      setIsLoading(false)
      showNotification({
        variant: 'danger',
        message: 'Could not mint the Avatar'
      })
    }
  }

  const handleMint = () => {
    setIsMintModalOpen(true)
  }

  return (
    <div className='h-full w-full'>
      {isLoading ? (
        <Center w='100%' h='100%'>
          <Spinner isFull />
        </Center>
      ) : (
        <>
          <Text hiddenFrom='md' fz='xs'>
            STEP {step + 1}/2
          </Text>
          <OnboardingHeader title={TENANT_ONBOARDING.avatar.title} subTitle={TENANT_ONBOARDING.avatar.subtitle} />
          <AvatarWithInventory isDNA>
            <Group justify='space-between'>
              <Button miw={120} onClick={logout} variant='outline' disabled={isLoading} loading={isLoading}>
                {t('Cancel')}
              </Button>
              <Button miw={120} onClick={handleMint} disabled={isLoading} loading={isLoading}>
                {t('Mint')}
              </Button>
            </Group>
          </AvatarWithInventory>

          <EnhancedPaper withBg w='100%' className='md:absolute fixed bottom-0 left-0' visibleFrom='md'>
            <Divider mb='md' />
            <Group justify='end' px='sm' mb='md'>
              <Button
                data-testid='create-avatar-mint-button'
                miw={100}
                onClick={handleMint}
                disabled={isLoading}
                loading={isLoading}
              >
                {t('Mint')}
              </Button>
            </Group>
          </EnhancedPaper>

          <Modal opened={isMintModalOpen} onClose={() => setIsMintModalOpen(false)} maw={400} radius='md'>
            <ConfirmModal
              disabled={false}
              setMintType={setMintType}
              mintType={mintType}
              isLoading={isLoading}
              action={hasBalanceToSponsor ? sponsoredMint : gasMint}
              close={() => setIsMintModalOpen(false)}
              isSponsored={hasBalanceToSponsor}
            />
          </Modal>
        </>
      )}
    </div>
  )
}

const ConfirmModal = memo(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  withWaitingRoom(({ close, isLoading, disabled, nextStep, isSponsored }: any) => {
    const t = useTranslation()

    return (
      <Stack gap='xs' p='sm' mt='lg'>
        <Title order={3}>{t("You're about to Mint!")}</Title>
        <Text>{t(CONFIRM_MODAL_DESCRIPTION)}</Text>
        {!isSponsored && <BridgeLink />}

        <Group justify='space-between' mt='md'>
          <Button variant='outline' onClick={close}>
            {t('Cancel')}
          </Button>
          <Button
            data-testid='create-avatar-modal-continue-button'
            onClick={nextStep}
            disabled={disabled || isLoading}
            loading={isLoading}
          >
            {t('Continue')}
          </Button>
        </Group>
      </Stack>
    )
  })
)

export default MintAvatarOnchain
